import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Fade from '@mui/material/Fade'
import TextField from '@mui/material/TextField'
import { useState } from 'react'
import { useAuth } from '../App'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'

import EmailIcon from '@mui/icons-material/Email'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import GoogleIcon from '@mui/icons-material/Google'
import MicrosoftIcon from '@mui/icons-material/Microsoft'
import { Divider, InputAdornment, Tooltip, Typography } from '@mui/material'
import { Link } from 'react-router-dom'

import LogoAsr from '../assets/logo_voxist_asr.svg'
import backgroundLog from '../assets/backlog.svg'
import { usePageTitle } from '../hooks/usePageMeta'
import { tokens } from '../theme/tokens'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const Home = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const auth = useAuth()
  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  usePageTitle('')

  const handleEmailBlur = () => {
    if (email && !EMAIL_REGEX.test(email)) {
      setEmailError(t('invalidEmail', 'Please enter a valid email address'))
    } else {
      setEmailError('')
    }
  }

  const handlePasswordBlur = () => {
    if (password && password.length < 6) {
      setPasswordError(t('passwordTooShort', 'Password must be at least 6 characters'))
    } else {
      setPasswordError('')
    }
  }

  const handleSignIn = async () => {
    setIsLoading(true)
    try {
      await auth?.signIn(email, password)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Fade in timeout={300}>
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #004a57 0%, #00758a 50%, #004a57 100%)',
        backgroundImage: `url(${backgroundLog})`,
        backgroundAttachment: 'fixed',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        '&::after': isDark
          ? {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              pointerEvents: 'none',
            }
          : {},
      }}
    >
      {/* Glass card */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 420,
          mx: 2,
          borderRadius: `${tokens.radius.xl}px`,
          backdropFilter: 'blur(24px)',
          backgroundColor: isDark
            ? 'rgba(10, 42, 48, 0.75)'
            : 'rgba(255, 255, 255, 0.82)',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          boxShadow: isDark
            ? '0 8px 40px rgba(0,0,0,0.6)'
            : '0 8px 40px rgba(0,0,0,0.18)',
          padding: { xs: 3, sm: 5 },
        }}
      >
        {/* Logo */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
          <img src={LogoAsr} style={{ width: 96, height: 96 }} alt="Voxist logo" />
          <Typography variant="h5" fontWeight="bold" textAlign="center" sx={{ mt: 1 }}>
            {t('appTitle')}
          </Typography>
          <Typography variant="caption" textAlign="center" sx={{ mt: 0.5, mb: 3, color: 'text.secondary' }}>
            {t('tagline')}
          </Typography>
        </Box>

        <Box component="form" noValidate aria-label="Sign in form">
          {/* OAuth buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              fullWidth
              size="large"
              onClick={() => auth?.signInWithGoogle()}
              variant="contained"
              startIcon={<GoogleIcon />}
              sx={{
                background: isDark ? '#8ab4f8' : '#4285f4',
                color: isDark ? '#1a1a1a' : '#ffffff',
                '&:hover': {
                  background: isDark ? '#aac8ff' : '#357ae8',
                },
                textTransform: 'none',
                fontWeight: 500,
                py: 1.5,
              }}
            >
              {t('signInWithGoogle')}
            </Button>

            <Button
              fullWidth
              size="large"
              onClick={() => auth?.signInWithMicrosoft()}
              variant="contained"
              startIcon={<MicrosoftIcon />}
              sx={
                isDark
                  ? {
                      background: 'transparent',
                      color: '#ffffff',
                      border: '1px solid rgba(255,255,255,0.5)',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.08)',
                      },
                      textTransform: 'none',
                      fontWeight: 500,
                      py: 1.5,
                    }
                  : {
                      background: '#2F2F2F',
                      color: '#ffffff',
                      '&:hover': {
                        background: '#262626',
                      },
                      textTransform: 'none',
                      fontWeight: 500,
                      py: 1.5,
                    }
              }
            >
              {t('signInWithMicrosoft')}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t('orUseExistingAccount', 'or use your account')}
            </Typography>
          </Divider>

          {/* Email / password fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              autoComplete="email"
              name="mail"
              label="Email"
              type="email"
              value={email}
              error={Boolean(emailError)}
              helperText={emailError}
              inputProps={{ 'aria-describedby': emailError ? 'email-error' : undefined }}
              FormHelperTextProps={{ id: 'email-error' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon aria-hidden="true" />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleEmailBlur}
              placeholder="john.doe@gmail.com"
              size="small"
            />

            <TextField
              fullWidth
              autoComplete="current-password"
              name="password"
              label="Password"
              type="password"
              value={password}
              error={Boolean(passwordError)}
              helperText={passwordError}
              inputProps={{ 'aria-describedby': passwordError ? 'password-error' : undefined }}
              FormHelperTextProps={{ id: 'password-error' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOpenIcon aria-hidden="true" />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={handlePasswordBlur}
              placeholder="••••••••"
              size="small"
            />

            {/* Forgot password */}
            <Box sx={{ textAlign: 'right' }}>
              <Tooltip title={t('resetPassword', 'Reset password')}>
                <Link
                  to="/resetLink"
                  style={{
                    textDecoration: 'none',
                    color: isDark ? tokens.colors.primary.light : tokens.colors.primary.darkText,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {t('forgotPassword', 'Forgot password?')}
                  </Typography>
                </Link>
              </Tooltip>
            </Box>

            <Button
              fullWidth
              size="medium"
              onClick={handleSignIn}
              variant="outlined"
              color="secondary"
              disabled={isLoading}
              aria-busy={isLoading}
              sx={{
                textTransform: 'none',
                py: 1,
                borderRadius: `${tokens.radius.pill}px`,
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('signInWithEmail')
              )}
            </Button>
          </Box>
        </Box>

        {/* Footer */}
        <Typography
          variant="caption"
          textAlign="center"
          display="block"
          sx={{ mt: 4, color: 'text.secondary' }}
        >
          {`\u00a9 ${new Date().getFullYear()} Voxist`}
        </Typography>
      </Box>
    </Box>
    </Fade>
  )
}

export default Home
